import { urlFor, urlWithoutParamsFor } from '@/sanity/sanityClient';
import Image from 'next/image';
import { ImageMeta, isSanityImage } from '@/shared/types/imageMeta';
import skeletonStyles from './Skeleton.module.scss';

interface HIHImageProps {
  image: ImageMeta;
  alt?: string;
  className?: string;
  width?: number;
  height?: number;
  fill?: boolean;
  sizes?: string;
  // Puts a loading placeholder behind the image so the box is not empty while it
  // loads. Only applies to `fill` images (content cards); fixed-size images (logos,
  // icons) are left untouched. Disable it for images with transparent areas or
  // `object-fit: contain` — the placeholder would stay visible around them.
  hasPlaceholder?: boolean;
  priority?: boolean;
  lazy?: boolean;
}

// Default source width for responsive fill images. The actual rendered size is
// controlled by CSS (object-fit:cover in a sized parent); this only caps the
// downloaded resolution while keeping the source aspect ratio intact.
const RESPONSIVE_FILL_WIDTH = 1600;

const HIHImage: React.FC<HIHImageProps> = ({
  image,
  className,
  width,
  height,
  alt,
  fill = false,
  sizes = '100vw',
  hasPlaceholder = true,
  priority = false,
  lazy = true,
}) => {
  if (!image) return null;

  // Responsive fill: no fixed px dimensions — the parent element provides
  // position:relative + the box size. Used for object-fit:cover images whose
  // source aspect ratio differs from the rendered box, so Next.js must not
  // compare against fixed width/height attributes.
  const isResponsiveFill = fill && (width === undefined || height === undefined);

  // `priority` already implies eager loading; setting `loading` alongside it is
  // an error in next/image, so omit it when the image is prioritized.
  const loading = priority ? undefined : lazy ? 'lazy' : 'eager';

  // Resolve the final src for both sources: REST serves a plain URL, Sanity
  // needs the image builder (responsive fill caps width + quality, fixed sizes
  // request the exact dimensions).
  let src: string | undefined;
  if (isSanityImage(image)) {
    src = image.image
      ? isResponsiveFill
        ? urlWithoutParamsFor(image.image).width(RESPONSIVE_FILL_WIDTH).quality(75).url()
        : urlFor(image.image, width as number, height as number).url()
      : undefined;
  } else {
    src = image.imageUrl;
  }

  if (!src) return null;

  const altText = alt ?? image.alt ?? '';

  // The placeholder is the image's own background and needs no load state: an image
  // without a decoded bitmap is transparent, so the background shows through until
  // the image covers it. Living on the <img> means it is clipped by whatever
  // border-radius the consumer sets on the image, and it fails safe — a broken image
  // keeps the placeholder instead of leaving an empty box.
  const imageClasses = [hasPlaceholder ? skeletonStyles.skeleton : undefined, className]
    .filter(Boolean)
    .join(' ');

  if (isResponsiveFill) {
    return (
      <Image
        src={src}
        alt={altText}
        className={imageClasses}
        loading={loading}
        fill
        sizes={sizes}
        priority={priority}
      />
    );
  }

  if (fill) {
    return (
      <div style={{ position: 'relative', width: `${width}px`, height: `${height}px` }}>
        <Image
          src={src}
          alt={altText}
          className={imageClasses}
          loading={loading}
          fill
          priority={priority}
        />
      </div>
    );
  }

  // Fixed width/height (logos, icons, thumbnails): rendered exactly as before,
  // no placeholder — wrapping these would break `width: 100%` styling.
  return (
    <Image
      src={src}
      alt={altText}
      width={width}
      height={height}
      className={className}
      priority={priority}
      quality={100}
    />
  );
};

export default HIHImage;
